import { BigNumberOccurrence } from "../../../models/occurrence";
import { useLocation } from "react-router-dom";
import { formatMilisecondsInDaysHours } from "../../../utils/dateUtils";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

const useIsColumn = (breakpoint = 1024) => {
  const [isColumn, setIsColumn] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsColumn(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isColumn;
};

const fitText = (el: HTMLElement, max = 20, min = 10): number => {
  el.style.fontSize = `${max}px`;
  let size = max;
  while (size > min && el.scrollWidth > el.clientWidth) {
    size -= 0.5;
    el.style.fontSize = `${size}px`;
  }
  return size;
};

const useFitTextSync = (
  containerRef: React.RefObject<HTMLDivElement>,
  ref1: React.RefObject<HTMLDivElement>,
  ref2: React.RefObject<HTMLDivElement>,
  deps: any[]
) => {
  const fit = () => {
    if (!ref1.current || !ref2.current) return;
    const s1 = fitText(ref1.current);
    const s2 = fitText(ref2.current);
    const minSize = Math.min(s1, s2);
    ref1.current.style.fontSize = `${minSize}px`;
    ref2.current.style.fontSize = `${minSize}px`;
  };

  useLayoutEffect(() => {
    fit();
  }, deps);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
};

export const BigNumbers = (props: {
  bigNumberAnalytics: BigNumberOccurrence;
  finishedFilter: string[];
  loading?: boolean;
  isFilteredByStep?: boolean | null;
}) => {
  const location = useLocation();
  const isHistorico = location.pathname.includes("history");

  const colorPrimary = isHistorico ? "text-color-dark" : "text-color-primary";
  const colorWarning = isHistorico ? "text-color-dark" : "text-color-warning";

  const quantityWidgets = () => [
    (props.finishedFilter.includes('true') || props.finishedFilter.length === 0) && {
      label: "Chamados concluídos",
      value: Math.max(props.bigNumberAnalytics.totalOfFinalizedOccurrence, 0),
      color: colorPrimary,
    },
    (!props.isFilteredByStep && props.finishedFilter.includes('false') || props.finishedFilter.length === 0) && {
      label: "Chamados não iniciados",
      value: Math.max(props.bigNumberAnalytics.totalOfNotStartedOccurrence, 0),
      color: colorPrimary,
    },
    (props.finishedFilter.includes('false') || props.finishedFilter.length === 0) && {
      label: "Chamados em andamento",
      value: Math.max(props.bigNumberAnalytics.totalOfInProgressOccurrence, 0),
      color: colorPrimary,
    },
    {
      label: "Veículos",
      value: Math.max(props.bigNumberAnalytics.totalChassis ?? 0, 0),
      color: colorPrimary,
    },
  ].filter(el => el !== false);

  const timeWidgets = [
    { label: "Tempo total em serviço", value: formatMilisecondsInDaysHours(props.bigNumberAnalytics.totalInService * 1000) || '0h', color: colorWarning },
    { label: "Tempo médio por veículo", value: formatMilisecondsInDaysHours(props.bigNumberAnalytics.averageInService * 1000) || '0h', color: colorWarning },
    { label: "Tempo médio por chamado", value: formatMilisecondsInDaysHours(props.bigNumberAnalytics.averageInServicePerOccurrence * 1000) || '0h', color: colorWarning },
  ];

  const isColumn = useIsColumn(1024);
  const containerRef = useRef<HTMLDivElement>(null);
  const card1Ref = useRef<HTMLDivElement>(null);
  const card2Ref = useRef<HTMLDivElement>(null);
  const q = quantityWidgets();
  useFitTextSync(containerRef, card1Ref, card2Ref, [q.length, props.loading, props.bigNumberAnalytics, isColumn]);

  const cardStyle = (itemCount: number): React.CSSProperties => ({
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 16px',
    overflow: 'hidden',
    minWidth: 0,
  });


  const renderItems = (items: { label: string; value: any; color: string }[]) =>
    items.map((item, index) => (
      <div key={index} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 10px' }}>
          <span className={item.color} style={{ fontSize: '2.4em', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            {props.loading ? '-' : item.value}
          </span>
          <span style={{ fontSize: '1em', color: '#555', whiteSpace: 'nowrap', marginTop: 2 }}>
            {item.label}
          </span>
        </div>
        {index !== items.length - 1 && (
          <div style={{ width: 1, height: '2.5em', background: '#ccc', flexShrink: 0 }} className="kanban-bn-cards-separator" />
        )}
      </div>
    ));

  return (
    <div ref={containerRef} className="flex gap-6" style={{ flexDirection: isColumn ? 'column' : 'row', alignItems: isColumn ? 'stretch' : 'center', width: '100%' }}>

      <div ref={card1Ref} style={cardStyle(q.length)} className="kanban-bn-cards">
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
          {renderItems(q)}
        </div>
      </div>

      <div ref={card2Ref} style={cardStyle(timeWidgets.length)} className="kanban-bn-cards">
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
          {renderItems(timeWidgets)}
        </div>
      </div>

    </div>
  );
};
